import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { buildTheme } from './theme';
import { Layout } from './components/layouts';
import MobileLayout from './components/layouts/MobileLayout';
import useIsMobile from './hooks/useIsMobile';
import useAppUpdateNotifier from './hooks/useAppUpdateNotifier';
import UpdateNotesDialog from './components/common/UpdateNotesDialog';
import { HomePage, ProjectsPage, LoginPage, AuthSuccessPage, AuthErrorPage, DatabasePage, OffersPage, OfferDetailPage, EstimatesPage, EstimateDetailPage, Estimates2Page, Estimates2DetailPage, SettingsPage, ProfilePage, SearchPage, HelpPage, ExpensesPage, ContractsPage, InvoicesPage, InvoiceApprovePage, TestingPage, MaterialApprovalPage, MaterialApprovalApprovePage, SchedulePage, TestPage, BoardPage, BoardRedirectPage, HumanResourcesPage, CareersPage, CareerDetailPage, CareerApplyPage, CareerVerifyPage, DocumentsPage, CandidatesForOfferPage, CandidatesAllPage, MapsPage, ProjectDetailPage, RequesterPage, GusPage, TaskerPage, TranscriptionPage, NotesPage, NotesTranscriptionPage, MyDocsPage, BugReportPage, ServicePage, ContractSignPage, QRGeneratorPage, EquipmentPage, BiometricsPage, VisionPage, CadViewerPage, CadProjectPage } from './pages';
import OtherExpensesPage from './pages/OtherExpensesPage';
import EmployeePayoutsPage from './pages/EmployeePayoutsPage';
import TaxesZusPage from './pages/TaxesZusPage';
import AppLandingPage from './pages/AppLandingPage';
import KadryPage from './pages/KadryPage';
import KadryEmployeePage from './pages/KadryEmployeePage';
import EasyEstimatePage from './pages/EasyEstimatePage';
import EasyEstimateOfferKosztorysyPage from './pages/EasyEstimateOfferKosztorysyPage';
import EasyEstimateEstimateDetailPage from './pages/EasyEstimateEstimateDetailPage';
import EasyEstimateVerifiedPositionsPage from './pages/EasyEstimateVerifiedPositionsPage';
import EasyEstimateAllVerifiedPositionsPage from './pages/EasyEstimateAllVerifiedPositionsPage';
import ScheduleProjectListPage from './pages/ScheduleProjectListPage';
import ScheduleDetailPage from './pages/ScheduleDetailPage';
import InvoiceFormPage from './pages/InvoiceFormPage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import NotificationProviderPage from './components/layouts/NotificationProviderPage';
import ScreenShareViewerPage from './pages/ScreenShareViewerPage';
 
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RequirePermission from './components/auth/RequirePermission';
import RequireAnyPermission from './components/auth/RequireAnyPermission';
import MaintenanceOverlay from './components/common/MaintenanceOverlay';
import LegacyHostBanner from './components/common/LegacyHostBanner';

const App: React.FC = () => {
  const [mode, setMode] = React.useState<'light'|'dark'>(() => {
    try { return (localStorage.getItem('ui:theme:mode') as any) || 'light'; } catch { return 'light'; }
  });
  React.useEffect(() => {
    const onToggle = (e: any) => { if (e?.detail?.mode) { setMode(e.detail.mode); try{ localStorage.setItem('ui:theme:mode', e.detail.mode);}catch{} } };
    window.addEventListener('ui:theme:toggle', onToggle as any);
    return () => window.removeEventListener('ui:theme:toggle', onToggle as any);
  }, []);
  const theme = React.useMemo(() => buildTheme(mode), [mode]);
  const isMobile = useIsMobile(768);
  const { open, versionInfo, changelog, handleClose } = useAppUpdateNotifier();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LegacyHostBanner />
      <MaintenanceOverlay />
      <UpdateNotesDialog open={open} onClose={handleClose} versionInfo={versionInfo} changelog={changelog} />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Strony publiczne */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/success" element={<AuthSuccessPage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />
            {/* Careers public pages */}
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/careers/:slug" element={<CareerDetailPage />} />
            <Route path="/careers/:slug/apply" element={<CareerApplyPage />} />
            <Route path="/careers/verify" element={<CareerVerifyPage />} />
            {/* legacy singular */}
            <Route path="/career" element={<Navigate to="/careers" replace />} />
            {/* Public documents */}
            <Route path="/documents" element={<DocumentsPage />} />
            {/* Umowy do podpisu - publiczna strona z tokenem */}
            <Route path="/contracts/sign/:token" element={<ContractSignPage />} />
            {/* Tymczasowa strona testu bez tokenu */}
            <Route path="/test" element={<TestPage />} />
            {/* Pusta strona GUS – publiczna */}
            <Route path="/gus" element={<GusPage />} />
            
            {/* Strony chronione - wymagają uwierzytelnienia */}
            <Route element={<ProtectedRoute />}>
              {/* Strony pełnoekranowe (bez Layout) – z NotificationProvider dla powiadomień */}
              <Route element={<RequirePermission permission="invoices" />}>
                <Route path="/accounting/new" element={<NotificationProviderPage><InvoiceFormPage /></NotificationProviderPage>} />
                <Route path="/accounting/edit/:id" element={<NotificationProviderPage><InvoiceEditPage /></NotificationProviderPage>} />
              </Route>

              <Route element={isMobile ? <MobileLayout /> : <Layout />}>
                <Route element={<RequirePermission permission="home" />}> 
                  <Route path="/" element={<HomePage />} />
                </Route>
                <Route element={<RequirePermission permission="projects" />}>
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:id" element={<ProjectDetailPage />} />
                </Route>
                <Route element={<RequirePermission permission="database" />}>
                  <Route path="/database" element={<DatabasePage />} />
                </Route>
                <Route element={<RequirePermission permission="offers" />}>
                  <Route path="/offers" element={<OffersPage />} />
                  <Route path="/offers/:id" element={<OfferDetailPage />} />
                </Route>
                {/* Moduł Umowy – na razie bez osobnych uprawnień, dostępny dla zalogowanych */}
                <Route element={<RequirePermission permission="contracts" />}>
                  <Route path="/app/contracts" element={<ContractsPage />} />
                </Route>
                {/* Moduł Wyposażenie */}
                <Route path="/app/equipment" element={<EquipmentPage />} />
                <Route element={<RequirePermission permission="invoices" />}>
                  <Route path="/accounting" element={<Navigate to="/accounting/purchases" replace />} />
                  <Route path="/accounting/sales" element={<InvoicesPage />} />
                  <Route path="/accounting/warehouses" element={<InvoicesPage />} />
                  <Route path="/accounting/purchases" element={<InvoicesPage />} />
                  <Route path="/accounting/settlements" element={<InvoicesPage />} />
                  <Route path="/accounting/bank-operations" element={<InvoicesPage />} />
                  <Route path="/accounting/bank-statements" element={<InvoicesPage />} />
                  <Route path="/accounting/other-expenses" element={<OtherExpensesPage />} />
                  <Route path="/accounting/employee-payouts" element={<EmployeePayoutsPage />} />
                  <Route path="/accounting/taxes-zus" element={<TaxesZusPage />} />
                  <Route path="/accounting/hermes-export" element={<InvoicesPage />} />
                  <Route path="/accounting/approve/:id" element={<InvoiceApprovePage />} />
                </Route>
                {/* Landing Aplikacje – dostęp jeśli użytkownik ma przynajmniej jedną aplikację */}
                <Route element={<RequireAnyPermission anyOf={['estimates','materials_approvals','schedules','maps','requester','human_resources','tasker','contracts','kadry']} />}>
                  <Route path="/app" element={<AppLandingPage />} />
                </Route>
                {/* Moduł Kadry – zarządzanie pracownikami (userzy aplikacji + pracownicy fizyczni) */}
                <Route path="/app/kadry" element={<KadryPage />} />
                <Route path="/app/kadry/:id" element={<KadryEmployeePage />} />
                {/* Moduł board – przekierowanie do Collectifile */}
                <Route element={<RequirePermission permission="projects" />}>
                  <Route path="/board/:id" element={<BoardRedirectPage />} />
                </Route>
                {/* Aplikacje – uprawnienia per-aplikacja */}
                <Route element={<RequirePermission permission="estimates" />}>
                  <Route path="/app/estimates" element={<EstimatesPage />} />
                  <Route path="/app/estimates/:id" element={<EstimateDetailPage />} />
                  <Route path="/app/estimates2" element={<Estimates2Page />} />
                  <Route path="/estimates2" element={<Estimates2Page />} />
                  <Route path="/estimates2/:id" element={<Estimates2DetailPage />} />
                </Route>
                <Route element={<RequirePermission permission="materials_approvals" />}>
                  <Route path="/app/materials_approvals" element={<MaterialApprovalPage />} />
                  <Route path="/app/materials_approvals/:kzmId" element={<MaterialApprovalApprovePage />} />
                </Route>
                <Route element={<RequirePermission permission="schedules" />}>
                  <Route path="/app/schedules" element={<SchedulePage />} />
                  <Route
                    path="/app/schedules/project/:projectId"
                    element={<ScheduleProjectListPage />}
                  />
                  <Route
                    path="/app/schedules/project/:projectId/schedule/:id"
                    element={<ScheduleDetailPage />}
                  />
                </Route>
                <Route element={<RequirePermission permission="maps" />}>
                  <Route path="/app/maps" element={<MapsPage />} />
                </Route>
                {/* Notatki i transkrypcje */}
                <Route path="/app/notes" element={<NotesPage />} />
                <Route path="/app/notes/transcription" element={<NotesTranscriptionPage />} />
                {/* Redirecty ze starego adresu */}
                <Route path="/app/transcription" element={<Navigate to="/app/notes/transcription" replace />} />
                {/* Generator kodów QR */}
                <Route path="/app/qr-generator" element={<QRGeneratorPage />} />
                {/* EasyEstimate — osobny moduł kosztorysowy (bez modułu Kosztorysy) */}
                <Route
                  path="/app/easy-estimate/zweryfikowane"
                  element={<EasyEstimateAllVerifiedPositionsPage />}
                />
                <Route
                  path="/app/easy-estimate/estimate/:estimateId/zweryfikowane"
                  element={<EasyEstimateVerifiedPositionsPage />}
                />
                <Route path="/app/easy-estimate/estimate/:estimateId" element={<EasyEstimateEstimateDetailPage />} />
                <Route path="/app/easy-estimate/offer/:offerId" element={<EasyEstimateOfferKosztorysyPage />} />
                <Route path="/app/easy-estimate" element={<EasyEstimatePage />} />
                {/* Tasker (na razie bez osobnej kontroli uprawnień – dostępny dla zalogowanych) */}
                <Route path="/app/tasker" element={<TaskerPage />} />
                <Route element={<RequirePermission permission="requester" />}>
                  <Route path="/app/requester" element={<RequesterPage />} />
                </Route>
                {/* Redirect dla starej ścieżki */}
                <Route path="/estimates" element={<Navigate to="/app/estimates" replace />} />
                <Route path="/app/material-approval" element={<Navigate to="/app/materials_approvals" replace />} />
                <Route path="/app/material_approval" element={<Navigate to="/app/materials_approvals" replace />} />
                <Route path="/app/schedule" element={<Navigate to="/app/schedules" replace />} />
                <Route element={<RequirePermission permission="analytics" />}>
                  <Route path="/analytics" element={<div>Strona analityki w przygotowaniu</div>} />
                </Route>
                <Route element={<RequirePermission permission="testing" />}>
                  <Route path="/testing" element={<TestingPage />} />
                </Route>
                {/* Strona zgłaszania błędów – dostępna dla zalogowanych bez dodatkowych uprawnień */}
                <Route path="/bug-report" element={<BugReportPage />} />
                {/* Serwis usterkowy */}
                <Route element={<RequirePermission permission="service" />}>
                  <Route path="/service" element={<ServicePage />} />
                </Route>
                {/* Rejestr wydatków pracownika */}
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route element={<RequirePermission permission="permissions" />}>
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
                <Route element={<RequirePermission permission="human_resources" />}>
                  <Route path="/hr" element={<HumanResourcesPage />} />
                  <Route path="/hr/offers/:id/candidates" element={<CandidatesForOfferPage />} />
                  <Route path="/hr/candidates" element={<CandidatesAllPage />} />
                  <Route path="/app/hr" element={<HumanResourcesPage />} />
                </Route>
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/screenshare/viewer" element={<ScreenShareViewerPage />} />
                {/* Moje dokumenty */}
                <Route path="/mydocs" element={<MyDocsPage />} />
                {/* Biometrics (panel dodawania twarzy) */}
                <Route path="/biometrics" element={<BiometricsPage />} />
                {/* Vision (kamera + rozpoznanie) */}
                <Route path="/vision" element={<VisionPage />} />
                {/* Przeglądarka CAD – usunięta z Layout (pełny ekran) */}
              </Route>
              {/* Przeglądarka CAD – pełny ekran bez topbara i sidebara */}
              <Route path="/cad" element={<CadViewerPage />} />
              <Route path="/cad/" element={<CadViewerPage />} />
              <Route path="/cad/:projectOrOfferId" element={<CadProjectPage />} />
            </Route>
            
            {/* Przekierowanie dla nieznanych ścieżek */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
